import type { Task } from "@hq/contracts";
import { builtInViews, calendarDateOf, type NowContext, type TaskGroup } from "@hq/core";
import { Button, cn } from "@z/ui";
import { CircleCheck } from "lucide-react";
import { formatDayHeading } from "../format";
import { TaskList } from "../TaskList";
import { ViewHeader } from "../ViewHeader";
import { type ViewProps, viewClassName } from "./shared";
import { useViewState } from "./useViewState";

/** Groups by the day each task was completed in the device zone. Input order is kept within a day. */
export function groupByCompletionDay(tasks: Task[], now: NowContext): TaskGroup[] {
  const groups = new Map<string, TaskGroup>();
  for (const t of tasks) {
    const key = t.completedAt !== undefined ? calendarDateOf(t.completedAt, now.timeZone) : "~none";
    let g = groups.get(key);
    if (!g) {
      g = { key, label: key === "~none" ? "Unknown day" : formatDayHeading(key, now), tasks: [] };
      groups.set(key, g);
    }
    g.tasks.push(t);
  }
  return [...groups.values()].sort((a, b) => b.key.localeCompare(a.key));
}

export function CompletedView({
  onOpenTask,
  onGoToToday,
  now,
  className,
}: ViewProps & { onGoToToday?: () => void }) {
  const s = useViewState(builtInViews.completed);
  return (
    <div data-slot="completed-view" className={cn(viewClassName, className)}>
      <ViewHeader
        mode={s.mode}
        onModeChange={s.setMode}
        title={
          <span className="inline-flex items-center gap-2">
            <CircleCheck className="size-4 text-success" />
            Completed
          </span>
        }
        sort={s.sort}
        onSortChange={s.setSort}
        tags={s.tags}
        onTagsChange={s.setTags}
        availableTags={s.hasTasks ? s.allTags : []}
        displayControls={s.hasTasks && s.mode === "list"}
      />
      <TaskList
        view={s.view}
        mode={s.mode}
        onTagClick={s.toggleTag}
        groupWith={groupByCompletionDay}
        emptyTitle={s.tags.length ? "No completed tasks with this tag" : "Nothing completed yet"}
        emptyDescription={
          s.tags.length
            ? "Try another tag or clear the filter."
            : "Your finished tasks will be here when you need them."
        }
        emptyAction={
          s.tags.length ? (
            <Button variant="secondary" onClick={() => s.setTags([])}>
              Clear filters
            </Button>
          ) : onGoToToday ? (
            <Button variant="secondary" onClick={onGoToToday}>
              Go to Today
            </Button>
          ) : undefined
        }
        {...(onOpenTask ? { onOpenTask } : {})}
        {...(now ? { now } : {})}
      />
    </div>
  );
}
